/**
 * VERQO Onboarding-veiviser (PR3a).
 *
 * 3 steg for nye bedrifter:
 *   1. Konto (allerede opprettet i /auth) – vises som "fullført"
 *   2. Bedriftsinfo
 *   3. Velg moduler (minst én)
 *
 * Etter fullført steg 3:
 *   - `create_company_with_owner` oppretter company + owner-medlemskap + settings + 7 dagers trial
 *   - `activate_module_for_company` seed'er leverandører og kategorier for hver valgte modul
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowLeft, ArrowRight, Upload, PencilLine, Clock } from "lucide-react";
import { ACTIVATABLE_MODULES, type ModuleKey } from "@/modules";
import verqoLogo from "@/assets/verqo-logo.png.asset.json";
import { logOnboardingDebug, useActiveCompany, useAuthReady } from "@/hooks/use-active-company";
import { useTrialDays } from "@/hooks/use-trial-days";
import {
  MANUAL_IMPORT_NOTE,
  PRICE_IMPORT_HEADLINE,
  PRICE_FILE_SUPPLIER_EXAMPLES,
  PRICE_IMPORT_STEPS,
  SUPPORTED_PRICE_FILE_LABEL,
} from "@/lib/price-file-import";

/** Hva den nye brukeren vil gjøre med varer og priser rett etter oppstart. */
type PriceChoice = "upload" | "manual" | "later";

export const Route = createFileRoute("/onboarding")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Kom i gang | VERQO" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OnboardingPage,
});

type CompanyForm = {
  name: string;
  org_number: string;
  address: string;
  postal_code: string;
  city: string;
  phone: string;
  email: string;
  website: string;
};

const EMPTY_COMPANY: CompanyForm = {
  name: "", org_number: "", address: "", postal_code: "",
  city: "", phone: "", email: "", website: "",
};

function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<2 | 3>(2);
  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState<CompanyForm>(EMPTY_COMPANY);
  const [modules, setModules] = useState<Set<ModuleKey>>(new Set(["electrician"]));
  const [priceChoice, setPriceChoice] = useState<PriceChoice>("upload");
  const { user: authUser, isReady: authReady } = useAuthReady();
  const {
    user,
    company: activeCompany,
    companyId,
    hasCompanySettings,
    onboardingCompleted,
    isLoading: companyLoading,
    error: companyError,
  } = useActiveCompany();

  useEffect(() => {
    if (!authReady) return;
    if (!authUser) {
      navigate({ to: "/auth", replace: true });
      return;
    }
    setCompany((c) => ({ ...c, email: authUser.email ?? "" }));
    setChecking(false);
  }, [authReady, authUser, navigate]);

  useEffect(() => {
    logOnboardingDebug("onboarding", {
      authUser: user?.id ?? null,
      companyId: companyId || null,
      activeCompany: activeCompany?.companyName ?? null,
      onboardingCompleted,
      hasCompanySettings,
      onboardingRoute: "/onboarding",
      currentRoute: "/onboarding",
      loading: checking || companyLoading,
      error: companyError instanceof Error ? companyError.message : companyError ? String(companyError) : null,
    });

    if (checking || companyLoading) return;
    if (activeCompany) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [user, activeCompany, companyId, onboardingCompleted, hasCompanySettings, checking, companyLoading, companyError, navigate]);

  function toggleModule(k: ModuleKey) {
    setModules((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }

  async function handleFinish() {
    if (modules.size === 0) { toast.error("Velg minst én modul"); return; }
    if (!company.name.trim()) { toast.error("Firmanavn kreves"); setStep(2); return; }

    setSaving(true);
    try {
      const primary = Array.from(modules)[0];
      const { data: companyId, error } = await supabase.rpc("create_company_with_owner", {
        _name: company.name.trim(),
        _org_number: company.org_number.trim() || undefined,
        _address: company.address.trim() || undefined,
        _postal_code: company.postal_code.trim() || undefined,
        _city: company.city.trim() || undefined,
        _phone: company.phone.trim() || undefined,
        _email: company.email.trim() || undefined,
        _website: company.website.trim() || undefined,
        _active_module_key: primary,
      });
      if (error) throw error;
      if (!companyId) throw new Error("Ingen bedrift returnert");

      // Seed hver valgt modul (struktur kun – ingen priser eller produkter)
      for (const key of modules) {
        const def = ACTIVATABLE_MODULES.find((m) => m.key === key);
        if (!def) continue;
        const { error: seedErr } = await (supabase.rpc as any)("seed_module_for_company", {
          _company_id: companyId as unknown as string,
          _module_key: key,
          _seed: {
            suppliers: def.defaultSuppliers,
            categories: def.defaultMaterialCategories,
            checklist: def.defaultChecklistItems ?? [],
            standard_jobs: def.defaultStandardJobs ?? [],
          },
        });
        if (seedErr) throw seedErr;
      }

      toast.success("Bedrift opprettet – velkommen til VERQO!");
      if (priceChoice === "upload") {
        navigate({ to: "/settings/import/$entity", params: { entity: "materials" }, replace: true });
      } else if (priceChoice === "manual") {
        navigate({ to: "/materials", replace: true });
      } else {
        navigate({ to: "/dashboard", replace: true });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunne ikke opprette bedrift");
    } finally {
      setSaving(false);
    }
  }

  if (checking || companyLoading || activeCompany) {
    return (
      <div className="grid min-h-screen place-items-center" style={{ background: "#0D0D0F" }}>
        <Loader2 className="h-6 w-6 animate-spin text-white/60" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#0D0D0F" }}>
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-5 py-10">
        <div className="mb-6 flex flex-col items-center gap-3">
          <img src={verqoLogo.url} alt="VERQO" className="h-16 w-16 rounded-xl object-contain" />
          <h1 className="text-xl font-semibold text-white">Kom i gang med VERQO</h1>
        </div>

        <Stepper current={step} />

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur sm:p-8">
          {step === 2 && (
            <StepCompany
              value={company}
              onChange={setCompany}
              onNext={() => {
                if (!company.name.trim()) { toast.error("Firmanavn kreves"); return; }
                setStep(3);
              }}
            />
          )}

          {step === 3 && (
            <StepModules
              selected={modules}
              priceChoice={priceChoice}
              onPriceChoice={setPriceChoice}
              onToggle={toggleModule}
              onBack={() => setStep(2)}
              onFinish={handleFinish}
              saving={saving}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ current }: { current: number }) {
  const steps = [
    { n: 1, label: "Konto" },
    { n: 2, label: "Bedrift" },
    { n: 3, label: "Moduler" },
  ];
  return (
    <ol className="flex items-center justify-center gap-3">
      {steps.map((s, i) => {
        const done = s.n < current || s.n === 1;
        const active = s.n === current;
        return (
          <li key={s.n} className="flex items-center gap-2">
            <span
              className={`grid h-7 w-7 place-items-center rounded-full text-[11px] font-semibold ${
                done ? "bg-primary text-primary-foreground" :
                active ? "bg-white text-black" :
                "bg-white/10 text-white/50"
              }`}
            >
              {done ? <CheckCircle2 className="h-4 w-4" /> : s.n}
            </span>
            <span className={`text-xs ${active ? "text-white" : "text-white/50"}`}>{s.label}</span>
            {i < steps.length - 1 && <span className="mx-2 h-px w-8 bg-white/10" />}
          </li>
        );
      })}
    </ol>
  );
}

function StepCompany({
  value, onChange, onNext,
}: { value: CompanyForm; onChange: (v: CompanyForm) => void; onNext: () => void }) {
  const set = (k: keyof CompanyForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [k]: e.target.value });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onNext(); }} className="space-y-4">
      <h2 className="text-lg font-semibold text-white">Opprett bedrift</h2>
      <p className="text-sm text-white/50">Vi bruker denne informasjonen på tilbud, PDF-er og fakturaer.</p>

      <Field label="Firmanavn *" value={value.name} onChange={set("name")} required />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Organisasjonsnummer" value={value.org_number} onChange={set("org_number")} inputMode="numeric" />
        <Field label="Telefon" value={value.phone} onChange={set("phone")} inputMode="tel" />
      </div>
      <Field label="Adresse" value={value.address} onChange={set("address")} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Postnummer" value={value.postal_code} onChange={set("postal_code")} inputMode="numeric" />
        <Field label="Poststed" value={value.city} onChange={set("city")} />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="E-post" type="email" value={value.email} onChange={set("email")} />
        <Field label="Nettside" value={value.website} onChange={set("website")} placeholder="https://" />
      </div>

      <div className="flex justify-end pt-2">
        <button type="submit" className="btn btn-primary">
          Neste <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}

const PRICE_CHOICES: { key: PriceChoice; label: string; hint: string; icon: typeof Upload }[] = [
  { key: "upload", label: "Last opp prisfil", hint: "Åpner opplastingen for vare-/prisfil fra leverandøren din.", icon: Upload },
  { key: "manual", label: "Registrer manuelt", hint: "Gå til materiellregisteret og legg inn varer selv.", icon: PencilLine },
  { key: "later", label: "Gjør dette senere", hint: "Hopp over nå – knappen «Last opp prisfil» ligger i materiellregisteret.", icon: Clock },
];

function StepModules({
  selected, priceChoice, onPriceChoice, onToggle, onBack, onFinish, saving,
}: {
  selected: Set<ModuleKey>;
  priceChoice: PriceChoice;
  onPriceChoice: (c: PriceChoice) => void;
  onToggle: (k: ModuleKey) => void;
  onBack: () => void;
  onFinish: () => void;
  saving: boolean;
}) {
  const trialDays = useTrialDays();
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-white">Velg moduler</h2>
        <p className="text-sm text-white/50">
          Du kan aktivere flere moduler senere. Minst én må velges nå.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {ACTIVATABLE_MODULES.map((m) => {
          const active = selected.has(m.key);
          const Icon = m.icon;
          return (
            <button
              type="button"
              key={m.key}
              onClick={() => onToggle(m.key)}
              className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                active
                  ? "border-primary bg-primary/10"
                  : "border-white/10 bg-white/[0.03] hover:border-white/20"
              }`}
            >
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-white/10 text-lg">
                {m.emoji}
              </span>
              <span className="flex-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Icon className="h-4 w-4" /> {m.label}
                </span>
                <span className="mt-0.5 block text-xs text-white/50">
                  {m.defaultMaterialCategories.length} kategorier · {m.defaultSuppliers.length} leverandører
                </span>
              </span>
              <span className={`grid h-5 w-5 place-items-center rounded-full border ${
                active ? "border-primary bg-primary text-primary-foreground" : "border-white/20"
              }`}>
                {active && <CheckCircle2 className="h-3.5 w-3.5" />}
              </span>
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-white/50">
        Du starter på en <span className="font-semibold text-white">{trialDays} dagers gratis prøveperiode</span>.
        Ingen betalingsinfo kreves.
      </div>

      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-xs text-white/60">
        <div className="mb-2 text-sm font-semibold text-white">Bruk dine egne leverandørpriser</div>
        <p className="mb-3 leading-relaxed text-white/70">{PRICE_IMPORT_HEADLINE}</p>
        <ol className="list-decimal space-y-1 pl-4">
          {PRICE_IMPORT_STEPS.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <p className="mt-2">
          Eksempler på leverandører: {PRICE_FILE_SUPPLIER_EXAMPLES.join(", ")}. {MANUAL_IMPORT_NOTE} Filformat:
          {SUPPORTED_PRICE_FILE_LABEL}.
        </p>

        <div className="mt-4 grid gap-2">
          {PRICE_CHOICES.map((c) => {
            const Icon = c.icon;
            const active = priceChoice === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => onPriceChoice(c.key)}
                className={`flex items-start gap-3 rounded-lg border p-3 text-left transition ${
                  active ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/20"
                }`}
              >
                <Icon className="mt-0.5 h-4 w-4 text-white/70" />
                <span>
                  <span className="block text-sm font-medium text-white">{c.label}</span>
                  <span className="block text-xs text-white/50">{c.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button type="button" className="btn btn-ghost" onClick={onBack} disabled={saving}>
          <ArrowLeft className="h-4 w-4" /> Tilbake
        </button>
        <button type="button" className="btn btn-primary" onClick={onFinish} disabled={saving || selected.size === 0}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Fullfør
        </button>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, type = "text", required, inputMode, placeholder,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-white/60">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        inputMode={inputMode}
        placeholder={placeholder}
        className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none"
      />
    </div>
  );
}
